/**
 * Import Progress Indicator
 * Floating panel showing active import operations with real-time progress.
 *
 * COMPLETION IS OBSERVED, NOT HOPED FOR (I3). `activeImports` used to be
 * seeded once by `fetchSyncStatus()` and thereafter mutated ONLY by the SSE
 * `sync_progress` / `sync_completed` events, so a job that finished before,
 * between, or without those events never left the list and never reported
 * success — three identical imports produced three different outcomes (frozen
 * at 0%, a card, nothing at all). Now the panel POLLS `/imports/sync/status`
 * while anything is in flight and derives terminal outcomes from that snapshot
 * (`import-completion-observer.ts`). SSE is kept purely as an accelerator: it
 * makes the same conclusion arrive sooner, and when the stream is unavailable
 * entirely the panel still works, just with a poll's latency.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatDistanceToNow } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import {
  getJobStatus,
  syncSource,
} from '@/modules/accounting/features/import/services/import.service'
import { invalidateEverything } from '@/platform/query-client'
import { selectLiveSyncs } from '@/platform/sync/stores/accepted-jobs'
import { useMonitoringStore } from '@/platform/sync/stores/monitoringStore'
import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'
import type { SseSyncEvent } from '@/platform/sync/types'
import { syncClient, type ConnectionState } from '@/platform/websocket/syncClient'
import { logger } from '@/shared/utils/logger'

import { selectActiveJobIds, selectObservedImportOutcomes } from './import-completion-observer'
import {
  extractImportedRecordCount,
  extractUnknownRowsTicket,
  isJobKnownMissing,
  isJobNotFoundException,
  markJobAsMissing,
  type JobStatusWithTicketMetadata,
} from './import-ticket-metadata'
import { type FailedImport } from './ImportFailedItem'
import { type ActiveImport } from './ImportItem'
import { ImportProgressPanel } from './ImportProgressPanel'
import { useNow } from './ImportRowParts'
import { type CompletedImportEntry } from './RecentlyCompletedSection'
import { IMPORT_PROGRESS_PANEL_BODY_CLASS } from '../constants/overlay-layers'
import { formatSyncErrorForDisplay } from '../utils/format-sync-error'

/**
 * How often the panel re-reads `/imports/sync/status` while an import is in
 * flight. The backend's `activeSyncs` is exactly `status IN
 * ('pending','processing')`, so a finished job drops out of the next snapshot
 * — this is what makes completion observable without any SSE event. Polling
 * stops the moment nothing is running, so an idle session costs nothing.
 * Without the stream the poll IS the live feed (2 s); with it, a slower safety
 * net. The store coalesces overlapping fetches either way.
 */
const POLL_WITHOUT_STREAM_MS = 2000
const POLL_WITH_STREAM_MS = 5000

export const ImportProgressIndicator = () => {
  const { t, i18n } = useTranslation('imports')
  const { t: tCommon } = useTranslation('common')
  // Subscribe to state only — actions (`fetchSyncStatus`) are read
  // via `useMonitoringStore.getState()` inside SSE handlers to avoid
  // stale-closure bugs when the effect captures them at mount time.
  const syncStatus = useMonitoringStore((s) => s.syncStatus)
  // Jobs a 202 accepted that no snapshot lists yet: shown as queued at once.
  const acceptedJobs = useMonitoringStore((s) => s.acceptedJobs)

  // Locale lookup for `date-fns/formatDistanceToNow`. The i18next code
  // is typically `es` or `es-ES`; anything else falls back to English.
  const dateLocale = i18n.language?.startsWith('es') ? es : enUS
  const formatRelativeTime = (date: Date): string => {
    // Defense in depth: a malformed/missing startedAt (e.g. a partial SSE
    // event that slipped past the store guards) yields an Invalid Date, and
    // formatDistanceToNow would throw a RangeError — crashing this globally
    // mounted component. Render nothing instead.
    if (Number.isNaN(date.getTime())) return ''
    return formatDistanceToNow(date, { locale: dateLocale, addSuffix: true })
  }

  const [isExpanded, setIsExpanded] = useState(true)
  const [isMinimized, setIsMinimized] = useState(true)
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')
  const [completedImports, setCompletedImports] = useState<CompletedImportEntry[]>([])
  // Transient, session-scoped list of FAILED imports. Driven by the
  // `sync_failed` SSE event AND by an observed terminal 'failed' state, so a
  // failure is surfaced as an explicit error (never a green success bar, never
  // silently gone). Dismissible by the user.
  const [failedImports, setFailedImports] = useState<FailedImport[]>([])

  // Server-reported live jobs + the accepted ones it has not listed yet.
  const liveSyncs = useMemo(
    () => selectLiveSyncs(syncStatus, acceptedJobs),
    [syncStatus, acceptedJobs],
  )

  // The jobs running when the person closed the panel; null = not closed. A job outside it
  // (a new import) opens the panel again, expanded.
  const [closedWith, setClosedWith] = useState<ReadonlySet<string> | null>(null)
  useEffect(() => {
    if (closedWith && liveSyncs.some((sync) => !closedWith.has(sync.jobId))) {
      setClosedWith(null)
      setIsMinimized(false)
      setIsExpanded(true)
    }
  }, [closedWith, liveSyncs])
  const activeImports: ActiveImport[] = liveSyncs.map((sync) => ({
      jobId: sync.jobId,
      sourceName: sync.sourceName,
      type: sync.type,
      progress: sync.progress,
      recordsProcessed: sync.recordsProcessed,
      totalRecords: sync.totalRecords,
      currentStage: sync.currentStage,
      waitingFor: sync.waitingFor,
      waitingUntil: sync.waitingUntil,
      startedAt: new Date(sync.startedAt),
      status: sync.status as 'processing' | 'pending',
    }))

  const hasActiveImports = activeImports.length > 0
  // One shared second-clock for every row's elapsed time; off when idle.
  const now = useNow(hasActiveImports)
  // Polite screen-reader news: a job queued, finished or failed. Never the
  // ticking numbers (that would talk over the user every second).
  const [announcement, setAnnouncement] = useState('')
  // Read through refs inside the stable report callbacks below (a fresh `t`
  // per render must not re-create them and re-run the observer effect).
  const announceRef = useRef<(key: 'queued' | 'done' | 'failed', sourceName: string) => void>(
    () => {},
  )
  announceRef.current = (key, sourceName) =>
    setAnnouncement(
      t(`progressIndicator.announce.${key}`, {
        name: resolveSyncSourceLabel(sourceName, tCommon),
        defaultValue: key === 'queued' ? '{{name}}: queued' : key === 'done' ? '{{name}}: import completed' : '{{name}}: import failed',
      }),
    )

  // ⚠️ Mientras el panel se ve, el documento reserva hueco abajo: si no, este
  // panel fijo se queda encima de los controles que viven al final de la
  // página. Medido: tapaba la paginación entera de «Transacciones».
  const panelVisible = hasActiveImports || completedImports.length > 0 || failedImports.length > 0
  useEffect(() => {
    document.body.classList.toggle(IMPORT_PROGRESS_PANEL_BODY_CLASS, panelVisible)
    return () => document.body.classList.remove(IMPORT_PROGRESS_PANEL_BODY_CLASS)
  }, [panelVisible])

  // ---------------------------------------------------------------------
  // Observation bookkeeping
  // ---------------------------------------------------------------------

  /** Panel mount instant — completions older than this are history, not news. */
  const observedSinceRef = useRef<Date>(new Date())
  /** Job ids that were running in the PREVIOUS snapshot we looked at. */
  const watchedActiveJobIdsRef = useRef<Set<string>>(new Set())
  /** Job ids already surfaced (from either source) — never report one twice. */
  const reportedJobIdsRef = useRef<Set<string>>(new Set())

  const playNotificationSound = () => {
    const audio = new Audio('/sounds/notification.mp3')
    audio.play().catch(() => {
      // Ignore autoplay errors
    })
  }

  /**
   * Enrich an already-visible completion with its unknown-rows ticket and the
   * real record count. Deliberately AFTER the entry is rendered: the success
   * confirmation must never depend on this fetch succeeding.
   */
  const enrichCompletedImport = useCallback(
    (jobId: string, sourceName: string, completedAt: Date): void => {
      // Issue #311 defense-in-depth: short-circuit if we've already learned
      // this jobId is gone from the read side.
      if (isJobKnownMissing(jobId)) return
      void (async () => {
        try {
          const job = (await getJobStatus(jobId)) as JobStatusWithTicketMetadata
          const ticket = extractUnknownRowsTicket(job)
          const importedRecordCount = extractImportedRecordCount(job)
          setCompletedImports((prev) => {
            const existing = prev.find((entry) => entry.jobId === jobId)
            if (existing) {
              return prev.map((entry) =>
                entry.jobId === jobId ? { ...entry, ticket, importedRecordCount } : entry,
              )
            }
            // No entry yet — the completion was not reported as "this session"
            // (a replayed historical event). An unknown-rows AUTO-TICKET is
            // still worth surfacing whenever we learn about it: that banner is
            // an open support ticket the user must see (issue #305), not a
            // time-boxed "your import finished" confirmation. A ticket-less
            // historical completion stays silent.
            if (!ticket) return prev
            return [...prev, { jobId, sourceName, completedAt, importedRecordCount, ticket }]
          })
        } catch (error) {
          // Issue #311: a `404 JOB_NOT_FOUND` means the row has been purged
          // from the read side. Cache the jobId so repeated events for the same
          // stale job do not re-issue the 404 fetch, and stay completely silent
          // — this is an expected condition, not a bug.
          if (isJobNotFoundException(error)) {
            markJobAsMissing(jobId)
            return
          }
          // Other failures (network blip, 500) are unexpected — keep the warn so
          // they remain debuggable, but never re-throw and never hide the
          // completion we already showed.
          logger.warn('[ImportProgressIndicator] failed to fetch job status', { jobId, error })
        }
      })()
    },
    [],
  )

  const reportCompletedImport = useCallback(
    (jobId: string, sourceName: string, completedAt: Date): void => {
      if (reportedJobIdsRef.current.has(jobId)) return
      reportedJobIdsRef.current.add(jobId)
      setCompletedImports((prev) => {
        if (prev.some((entry) => entry.jobId === jobId)) return prev
        return [
          ...prev,
          { jobId, sourceName, completedAt, importedRecordCount: null, ticket: null },
        ]
      })
      // what the import brought changes every screen derived from the ledger (balances, tax)
      invalidateEverything()
      // Auto-expand so the user actually SEES the confirmation.
      setIsMinimized(false)
      setIsExpanded(true)
      announceRef.current('done', sourceName)
      if (useMonitoringStore.getState().soundNotifications) {
        playNotificationSound()
      }
    },
    [],
  )

  const reportFailedImport = useCallback(
    (
      jobId: string,
      sourceName: string,
      failedAt: Date,
      message: string,
      sourceId: string | null = null,
    ): void => {
      if (reportedJobIdsRef.current.has(jobId)) return
      reportedJobIdsRef.current.add(jobId)
      setFailedImports((prev) => {
        if (prev.some((entry) => entry.jobId === jobId)) return prev
        return [...prev, { jobId, sourceId, sourceName, failedAt, message }]
      })
      announceRef.current('failed', sourceName)
      setIsMinimized(false)
      setIsExpanded(true)
      if (useMonitoringStore.getState().soundNotifications) {
        playNotificationSound()
      }
    },
    [],
  )

  // Connect to SSE for real-time updates. Handler registrations capture
  // their unsubscribe handles so we don't leak listeners across mounts
  // (StrictMode double-mounts would otherwise duplicate them). The
  // shared `syncClient` is ref-counted: `disconnect()` only tears down
  // the underlying EventSource when the last consumer releases it, so
  // unmounting this component while other consumers (e.g.
  // `SyncNotifications`) are alive will not kill the stream.
  useEffect(() => {
    const unsubscribeConnState = syncClient.onConnectionStateChange((state) => {
      setConnectionState(state)
    })

    // Progress percentages arrive as SSE `sync_progress` events: the shared
    // syncClient delivers them already parsed (SseSyncEvent objects) and the
    // store action applies them onto `syncStatus.activeSyncs`, which this
    // component renders. They are an OPTIMIZATION on top of the poll below —
    // the panel is correct without them, just less smooth.
    const unsubscribeProgress = syncClient.on('sync_progress', (event) => {
      useMonitoringStore.getState().applySyncProgressEvent(event)
    })
    const unsubscribeCompleted = syncClient.on('sync_completed', handleSyncCompleted)
    const unsubscribeFailed = syncClient.on('sync_failed', handleSyncFailed)

    syncClient.connect()

    // Initial fetch so the widget can decide whether to render at all, and so
    // an import that finished moments ago (fast CSV) is still confirmed.
    useMonitoringStore.getState().fetchSyncStatus()

    return () => {
      unsubscribeConnState()
      unsubscribeProgress()
      unsubscribeCompleted()
      unsubscribeFailed()
      syncClient.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // POLL while anything is in flight. This is the fix for "the panel froze at
  // 0% for six minutes while the API said completed": the status endpoint is
  // the authority on what is still running, so we ask it instead of waiting
  // for an event that may never come (SSE down, event missed, job finished
  // between two ticks). Stops as soon as the active list empties.
  const streamLive = connectionState === 'connected'
  useEffect(() => {
    if (!hasActiveImports) return
    const intervalId = setInterval(
      () => {
        void useMonitoringStore.getState().fetchSyncStatus()
      },
      streamLive ? POLL_WITH_STREAM_MS : POLL_WITHOUT_STREAM_MS,
    )
    return () => clearInterval(intervalId)
  }, [hasActiveImports, streamLive])

  // Derive terminal outcomes from every status snapshot we see. The watch list
  // includes the ACCEPTED jobs: one that fails before any snapshot listed it
  // as running is still reported (from `failedSyncs`), never lost.
  useEffect(() => {
    const outcomes = selectObservedImportOutcomes({
      status: syncStatus,
      previouslyActiveJobIds: watchedActiveJobIdsRef.current,
      alreadyReportedJobIds: reportedJobIdsRef.current,
      observedSince: observedSinceRef.current,
      now: new Date(),
    })
    watchedActiveJobIdsRef.current = new Set([
      ...selectActiveJobIds(syncStatus),
      ...liveSyncs.map((sync) => sync.jobId),
    ])

    for (const outcome of outcomes) {
      if (outcome.outcome === 'completed') {
        reportCompletedImport(outcome.jobId, outcome.sourceName, outcome.at)
        enrichCompletedImport(outcome.jobId, outcome.sourceName, outcome.at)
      } else {
        // NEVER print the RAW text ("RPC call failed after 3 retries: HTTP error
        // 403") — English provider noise, sometimes with vendor URLs. Same
        // mapper the history row / failure toast / error modal already apply.
        reportFailedImport(
          outcome.jobId,
          outcome.sourceName,
          outcome.at,
          outcome.errorMessage && outcome.errorMessage.trim().length > 0
            ? formatSyncErrorForDisplay(outcome.errorMessage, t)
            : t('progressIndicator.failed.genericMessage', 'The import failed.'),
          outcome.sourceId ?? null,
        )
      }
    }
    // `t` is read fresh each run; re-running on its identity alone is harmless.
  }, [syncStatus, liveSyncs, reportCompletedImport, reportFailedImport, enrichCompletedImport, t])

  // Refresh status when indicator is opened. We read `fetchSyncStatus`
  // via `getState()` so we don't have to thread the action through the
  // dep array — Zustand actions are stable refs but reading from the
  // hook would re-fire this effect on any unrelated store change.
  useEffect(() => {
    if (!isMinimized) {
      useMonitoringStore.getState().fetchSyncStatus()
    }
  }, [isMinimized])

  // Auto-expand the panel the moment an import starts (`activeImports.length`
  // goes from 0 to ≥1). Without this, the panel boots minimized (`isMinimized=true`)
  // and the user only sees the header bar — they'd miss the live progress unless
  // they happened to click the chevron. Using a ref so we only react to the
  // transition, not to every render that keeps the count > 0.
  const previousActiveCountRef = useRef(0)
  useEffect(() => {
    const current = activeImports.length
    const previous = previousActiveCountRef.current
    previousActiveCountRef.current = current
    if (previous === 0 && current >= 1) {
      setIsMinimized(false)
      setIsExpanded(true)
    }
  }, [activeImports.length])

  // Announce each job the moment it is accepted (not on every snapshot).
  const announcedAcceptedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const job of Object.values(acceptedJobs ?? {})) {
      if (typeof job?.jobId !== 'string' || announcedAcceptedRef.current.has(job.jobId)) continue
      announcedAcceptedRef.current.add(job.jobId)
      announceRef.current('queued', job.sourceName)
    }
  }, [acceptedJobs])

  // Retry = start the source again; the new job shows up as queued at once
  // (the service registers it from the 202). A refusal rejects and the row
  // says so — no toast.
  const retryFailedImport = async (entry: FailedImport): Promise<void> => {
    if (!entry.sourceId) return
    await syncSource(entry.sourceId, { displayName: entry.sourceName })
    setFailedImports((prev) => prev.filter((item) => item.jobId !== entry.jobId))
  }

  const handleSyncCompleted = (event: SseSyncEvent) => {
    // Pull the action fresh from the store on every event to dodge the
    // stale-closure trap — the handler is registered once in the
    // mount-effect above and would otherwise capture the very first
    // render's `fetchSyncStatus` reference forever. The refetch alone is
    // enough for the observer above to conclude the job finished; the direct
    // report below just avoids waiting for that round-trip.
    useMonitoringStore.getState().fetchSyncStatus()

    if (event.eventType !== 'completed' || !event.jobId) return
    const jobId = event.jobId
    const completedAt = event.completedAt ? new Date(event.completedAt) : new Date()

    // Auto-expand on ANY completion event, exactly as before: an event means
    // activity the user should see, whether it becomes a card, a ticket
    // banner, or just an active-list refresh. Manual minimize sticks until
    // the next event.
    setIsMinimized(false)
    setIsExpanded(true)

    // Only a completion from THIS session is a confirmation. Issue #311
    // showed the stream can replay historical `sync_completed` events to a
    // freshly connected client; surfacing those would pop a "recently
    // completed" card for an import the user ran days ago on every page load.
    // Same `observedSince` rule the polled observer applies, so both paths
    // agree. An absent/unparseable timestamp means "just now" — the event
    // arrived live, which is the only way to hear about it.
    const isFromThisSession =
      Number.isNaN(completedAt.getTime()) ||
      completedAt.getTime() >= observedSinceRef.current.getTime()
    if (isFromThisSession) {
      // `sourceName` is optional on the worker-push relay's payload; '' is
      // what ImportCompletedItem/ImportFailedItem already resolve to the
      // localized "unknown source" label via resolveSyncSourceLabel.
      reportCompletedImport(jobId, event.sourceName ?? '', completedAt)
    }

    // Enrichment runs for EVERY completed event, reported or not: it owns the
    // issue #311 404 short-circuit/cache, and a transient 500 must stay
    // retryable on the next event (the fetch is the only thing that can
    // discover an unknown-rows ticket).
    enrichCompletedImport(jobId, event.sourceName ?? '', completedAt)
  }

  const dismissCompletedImport = (jobId: string) => {
    setCompletedImports((prev) => prev.filter((entry) => entry.jobId !== jobId))
  }

  const dismissFailedImport = (jobId: string) => {
    setFailedImports((prev) => prev.filter((entry) => entry.jobId !== jobId))
  }

  const handleSyncFailed = (event: SseSyncEvent) => {
    // See note on `handleSyncCompleted` — read the action fresh on every
    // SSE callback instead of capturing it from `useMonitoringStore()`.
    useMonitoringStore.getState().fetchSyncStatus()

    // Surface the failure as an explicit ERROR entry so the user SEES the
    // import failed — it must NEVER silently vanish or read as a green
    // "success". `event.error` is sanitized by the SSE broadcaster
    // (`sanitizeOutboundErrorText`) so it carries no SQL / tenant UUIDs — but
    // sanitized is NOT localized: still raw English provider text, so it takes
    // the same mapper as the observed path above. Missing → generic.
    if (!event.jobId) return
    const safeMessage =
      typeof event.error === 'string' && event.error.trim().length > 0
        ? formatSyncErrorForDisplay(event.error, t)
        : t('progressIndicator.failed.genericMessage', 'The import failed.')
    reportFailedImport(
      event.jobId,
      event.sourceName ?? '',
      new Date(event.completedAt ?? event.timestamp ?? new Date().toISOString()),
      safeMessage,
      event.sourceId ?? null,
    )
  }

  const clearFinished = () => {
    setCompletedImports([])
    setFailedImports([])
  }

  // Closed by the person: out of the way (its notifications cleared) until an import that was
  // not running when it was closed starts.
  const closePanel = () => {
    clearFinished()
    setClosedWith(new Set(liveSyncs.map((sync) => sync.jobId)))
  }

  if (closedWith) return null

  // Keep the indicator open while there are active imports OR while there are
  // recently finished imports the user has not dismissed yet.
  if (!hasActiveImports && completedImports.length === 0 && failedImports.length === 0) {
    return null
  }

  return (
    <ImportProgressPanel
      connectionState={connectionState}
      activeImports={activeImports}
      failedImports={failedImports}
      completedImports={completedImports}
      isExpanded={isExpanded}
      isMinimized={isMinimized}
      onToggleExpanded={() => setIsExpanded(!isExpanded)}
      onToggleMinimized={() => setIsMinimized(!isMinimized)}
      onMinimize={() => setIsMinimized(true)}
      now={now}
      announcement={announcement}
      formatRelativeTime={formatRelativeTime}
      onDismissFailed={dismissFailedImport}
      onDismissCompleted={dismissCompletedImport}
      onClearFinished={clearFinished}
      onClose={closePanel}
      onRetry={retryFailedImport}
    />
  )
}
